import React from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Editorial image carousel for case study cards.
 * Each `image` item: { src, objectPosition?, bg?, alt? }
 */
const CaseImageCarousel = ({ images = [], testId }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [selected, setSelected] = React.useState(0);

  React.useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    onSelect();
    emblaApi.on("select", onSelect);
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  if (!images.length) return null;

  return (
    <div className="relative" data-testid={`case-carousel-${testId}`}>
      <div
        className="lt-img-wrap aspect-[4/5] overflow-hidden"
        ref={emblaRef}
      >
        <div className="flex h-full">
          {images.map((img, i) => (
            <div
              key={i}
              className="relative flex-[0_0_100%] h-full"
              style={{ background: img.bg || "var(--lt-cream)" }}
            >
              {img.isVideo ? (
                <video
                  src={img.src}
                  className="w-full h-full object-cover"
                  muted
                  loop
                  playsInline
                  autoPlay
                  preload="metadata"
                />
              ) : (
                <img
                  src={img.src}
                  alt={img.alt || ""}
                  className="w-full h-full object-cover"
                  style={{ objectPosition: img.objectPosition || "center" }}
                  draggable={false}
                  loading="lazy"
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {images.length > 1 && (
        <>
          <button
            onClick={() => emblaApi && emblaApi.scrollPrev()}
            data-testid={`case-carousel-prev-${testId}`}
            aria-label="Previous image"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 grid place-items-center bg-[var(--lt-peace)]/85 text-[var(--lt-charcoal)] hover:bg-[var(--lt-peace)] transition-colors backdrop-blur-sm"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => emblaApi && emblaApi.scrollNext()}
            data-testid={`case-carousel-next-${testId}`}
            aria-label="Next image"
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 grid place-items-center bg-[var(--lt-peace)]/85 text-[var(--lt-charcoal)] hover:bg-[var(--lt-peace)] transition-colors backdrop-blur-sm"
          >
            <ChevronRight size={18} />
          </button>

          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => emblaApi && emblaApi.scrollTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`case-carousel-dot-${testId}-${i}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  selected === i
                    ? "w-6 bg-[var(--lt-peace)]"
                    : "w-1.5 bg-[var(--lt-peace)]/50 hover:bg-[var(--lt-peace)]/80"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default CaseImageCarousel;
